import { Event } from '@nostr/tools/wasm'
import { ComponentProps, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ImageWithLightbox from '@/components/ImageWithLightbox'
import { SecondaryPageLink } from '@/PageManager'
import { toNote, toProfile } from '@/lib/link'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowUpRight01Icon } from '@hugeicons/core-free-icons'
import type { PhrasingContent, Root, Text } from 'mdast'
import type { Plugin } from 'unified'
import { visit } from 'unist-util-visit'
import type { Components as RmComponents } from 'react-markdown'
import type { Data, Node } from 'unist'
import { EmbeddedMention, EmbeddedNote } from '@/components/Embedded'
import * as nip19 from '@nostr/tools/nip19'

// Extend the Components interface to include your custom component
interface Components extends RmComponents {
  nostr: React.ComponentType<{
    rawText: string
    bech32Id?: string
  }>
}

interface NostrNode extends Node {
  type: 'nostr'
  data: Data & {
    hName: string
    hProperties: ComponentProps<Components['nostr']>
  }
}

export default function Markdown({
  content,
  event,
  className
}: {
  content: string
  event?: Event
  className?: string
}) {
  const components = useMemo(
    () =>
      ({
        nostr: ({ rawText, bech32Id }) => <NostrNode rawText={rawText} bech32Id={bech32Id} />,
        a: ({ href, children, ...props }) => {
          if (!href) {
            return <span {...props} className="break-words" />
          }
          if (href.startsWith('nevent1') || href.startsWith('naddr1')) {
            return (
              <SecondaryPageLink
                to={toNote(href)}
                className="break-words underline text-foreground"
              >
                {children}
              </SecondaryPageLink>
            )
          }
          if (href.startsWith('npub1') || href.startsWith('nprofile1')) {
            return (
              <SecondaryPageLink
                to={toProfile(href)}
                className="break-words underline text-foreground"
              >
                {children}
              </SecondaryPageLink>
            )
          }
          return (
            <a
              {...props}
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="break-words inline-flex items-baseline gap-1"
            >
              {children} <HugeiconsIcon icon={ArrowUpRight01Icon} className="size-3" />
            </a>
          )
        },
        p: (props) => <p {...props} className="break-words" />,
        div: (props) => <div {...props} className="break-words" />,
        code: (props) => <code {...props} className="break-words whitespace-pre-wrap" />,
        img: (props) => (
          <ImageWithLightbox
            image={event ? { url: props.src || '', pubkey: event.pubkey } : props.src || ''}
            className="max-h-[80vh] sm:max-h-[50vh] object-contain my-0 mx-auto block"
            classNames={{
              wrapper: 'w-fit max-w-full'
            }}
          />
        )
      }) as Components,
    [event?.pubkey]
  )

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkNostr]}
        urlTransform={(url) => {
          if (url.startsWith('nostr:')) return url.slice(6)
          return url
        }}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

const NOSTR_REGEX =
  /nostr:(npub1[a-z0-9]{58}|nprofile1[a-z0-9]+|note1[a-z0-9]{58}|nevent1[a-z0-9]+|naddr1[a-z0-9]+)/g
const NOSTR_REFERENCE_REGEX =
  /\[[^\]]+\]\[(nostr:(npub1[a-z0-9]{58}|nprofile1[a-z0-9]+|note1[a-z0-9]{58}|nevent1[a-z0-9]+|naddr1[a-z0-9]+))\]/g

const remarkNostr: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || typeof index !== 'number') return

      const text = node.value

      // First, handle reference-style nostr links [text][nostr:...]
      const refMatches = Array.from(text.matchAll(NOSTR_REFERENCE_REGEX))
      // Then, handle direct nostr links that are not part of reference links
      const directMatches = Array.from(text.matchAll(NOSTR_REGEX)).filter((directMatch) => {
        return !refMatches.some(
          (refMatch) =>
            directMatch.index! >= refMatch.index! &&
            directMatch.index! < refMatch.index! + refMatch[0].length
        )
      })

      // Combine and sort matches by position
      const allMatches = [
        ...refMatches.map((match) => ({
          ...match,
          type: 'reference' as const,
          bech32Id: match[2],
          rawText: match[0]
        })),
        ...directMatches.map((match) => ({
          ...match,
          type: 'direct' as const,
          bech32Id: match[1],
          rawText: match[0]
        }))
      ].sort((a, b) => a.index! - b.index!)

      if (allMatches.length === 0) return

      const children: (Text | NostrNode)[] = []
      let lastIndex = 0

      for (const match of allMatches) {
        const matchStart = match.index!
        const matchEnd = matchStart + match[0].length

        // Add text before the match
        if (matchStart > lastIndex) {
          children.push({
            type: 'text',
            value: text.slice(lastIndex, matchStart)
          })
        }

        // Create custom nostr node with type information
        const nostrNode: NostrNode = {
          type: 'nostr',
          data: {
            hName: 'nostr',
            hProperties: {
              bech32Id: match.bech32Id,
              rawText: match.rawText
            }
          }
        }
        children.push(nostrNode)

        lastIndex = matchEnd
      }

      // Add remaining text after the last match
      if (lastIndex < text.length) {
        children.push({
          type: 'text',
          value: text.slice(lastIndex)
        })
      }

      // Type assertion to tell TypeScript these are valid AST nodes
      parent.children.splice(index, 1, ...(children as PhrasingContent[]))
    })
  }
}

function NostrNode({ rawText, bech32Id }: ComponentProps<Components['nostr']>) {
  const { type, id } = useMemo(() => {
    if (!bech32Id) return { type: 'invalid', id: '' }
    try {
      const { type } = nip19.decode(bech32Id)
      if (type === 'npub') {
        return { type: 'mention', id: bech32Id }
      }
      if (type === 'nevent' || type === 'naddr' || type === 'note') {
        return { type: 'note', id: bech32Id }
      }
    } catch (error) {
      console.error('Invalid bech32 ID:', bech32Id, error)
    }
    return { type: 'invalid', id: '' }
  }, [bech32Id])

  if (type === 'invalid') return rawText

  if (type === 'mention') {
    return <EmbeddedMention user={id} className="not-prose" />
  }
  return <EmbeddedNote noteId={id} className="not-prose" />
}
